import React, { ReactNode } from "react";
import "./Button.css";

interface ButtonProps {
  children: ReactNode;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "outline" | "danger";
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: "button" | "submit" | "reset";
  loading?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  size = "md",
  variant = "primary",
  startIcon,
  endIcon,
  onClick,
  className = "",
  disabled = false,
  type = "button",
  loading = false,
}) => {
  const getButtonClasses = () => {
    const baseClasses = "btn";
    const sizeClass = `btn--${size}`;
    const variantClass = `btn--${variant}`;
    const disabledClass = disabled || loading ? "btn--disabled" : "";
    const loadingClass = loading ? "btn--loading" : "";

    return `${baseClasses} ${sizeClass} ${variantClass} ${disabledClass} ${loadingClass} ${className}`.trim();
  };

  return (
    <button
      type={type}
      className={getButtonClasses()}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading && (
        <span className="btn__spinner">
          <svg
            className="btn__spinner-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="31.416"
              strokeDashoffset="31.416"
            >
              <animate
                attributeName="stroke-dasharray"
                dur="2s"
                values="0 31.416;15.708 15.708;0 31.416"
                repeatCount="indefinite"
              />
              <animate
                attributeName="stroke-dashoffset"
                dur="2s"
                values="0;-15.708;-31.416"
                repeatCount="indefinite"
              />
            </circle>
          </svg>
        </span>
      )}
      {startIcon && !loading && <span className="btn__icon btn__icon--start">{startIcon}</span>}
      <span className="btn__content">{children}</span>
      {endIcon && !loading && <span className="btn__icon btn__icon--end">{endIcon}</span>}
    </button>
  );
};

export default Button;
